package ui.server.detail

import androidx.compose.runtime.*
import api.CurrencyType
import common.frontend.api.Io
import provider.CommonAccountProvider
import provider.CommonDisplayProvider
import provider.LocalAccountProvider
import provider.LocalDisplayProvider
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.globalViewModel
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tools.*
import ui.GlobalVM


interface ServerScope {

    val vm: ServerVM
    val vmGlobal: GlobalVM
    val action: (ServerVM.Action) -> Unit

    val args: ServerArgs
    val nav: Navigation
    val login: Io.User
    val display: CommonDisplayProvider
    val account: CommonAccountProvider

    companion object {
        @Composable
        fun rememberScope(): ServerScope {

            val nav = LocalNavigation.current
            val account = LocalAccountProvider.current
            val args = LocalNavigationArgs.current.rememberArgs(ServerArgs::class)
            val vm = nav.viewModel(ServerVM::class, args.serverId.toString())
            val vmGlobal = globalViewModel(GlobalVM::class)
            val action = ServerVM.Action.rememberAction(vm)
            val display = LocalDisplayProvider.current
            return remember {
                object : ServerScope {
                    override val vm: ServerVM
                        get() = vm
                    override val vmGlobal: GlobalVM
                        get() = vmGlobal
                    override val action: (ServerVM.Action) -> Unit
                        get() = action
                    override val args: ServerArgs
                        get() = args
                    override val nav: Navigation
                        get() = nav
                    override val login: Io.User
                        get() = account.current!!
                    override val display: CommonDisplayProvider
                        get() = display
                    override val account: CommonAccountProvider
                        get() = account


                }.also {
                    vm.init(it)
                }
            }
        }
    }

    class Wrapper(
        canEdit: Boolean,
        val canDelete: Boolean,
        val canSwap: Boolean,
        override val data: Io.Server,
        override val key: String = data.id.toString(),
    ) : EFieldList.Entry<Io.Server> {

        var editable by mutableStateOf(canEdit && !data.editLocked)
            private set

        val name = EFieldTextString(
            property = data::name,
            condition = { it.trim().takeIf { it.length > 2 } },
            onChange = { makeChange() })

//      COMPANY

        val companyName = EFieldTextString(
            property = data::companyName,
            condition = { it.trim().takeIf { it.length > 6 } },
            onChange = { makeChange() })

        val companyAddr = EFieldTextString(
            property = data::companyAddr,
            condition = { it.trim().takeIf { it.length > 6 } },
            onChange = { makeChange() })

        val companyVat = EFieldTextString(
            property = data::companyVat,
            condition = { it.trim().takeIf { it.length > 5 } },
            onChange = { makeChange() })

        val companyId = EFieldTextString(
            property = data::companyId,
            condition = { it.trim().takeIf { it.length > 5 } },
            onChange = { makeChange() })

//      RESPONSIBLE

        val responsibleName = EFieldTextString(
            property = data::responsibleName,
            condition = { it.trim().takeIf { it.split(" ").size > 1 } },
            onChange = { makeChange() })

        val responsibleTel = EFieldTextString(
            property = data::responsibleTel,
            condition = { it.trim().takeIf { it.length > 6 } },
            onChange = { makeChange() })

        val responsibleEmail = EFieldTextString(
            property = data::responsibleEmail,
            condition = { it.trim().takeIf { it.contains("@") && it.contains(".") } },
            onChange = { makeChange() })

        val responsibleWww = EFieldTextString(
            property = data::responsibleWww,
            condition = { it.trim().takeIf { it.contains(".") } },
            onChange = { makeChange() })

//      PRICE

        val pricePct = EFieldDualIntAsFloat(
            property = data::pricePct,
            onChange = { makeChange() })

        val priceCurrencyType = EFieldRegularInt(
            property = data::priceCurrencyType,
            onChange = { makeChange() })

        val pricePersonName = EFieldTextString(
            property = data::pricePerson,
            condition = { it.trim().takeIf { it.split(" ").size > 1 } },
            onChange = { makeChange() })

        val pricePersonMail = EFieldTextString(
            property = data::priceMail,
            condition = { it.trim().takeIf { it.contains("@") && it.contains(".") } },
            onChange = { makeChange() })
//      DOMAIN

        val domainTitle = EFieldTextString(
            property = data::domainTitle,
            condition = { it.trim().takeIf { it.length > 2 } },
            onChange = { makeChange() })

        val urlControl = EFieldTextString(
            property = data::domainUrl,
            condition = { it.trim().takeIf { it.length > 2 } },
            onChange = { makeChange() })

        val urlGame = EFieldTextString(
            property = data::gameUrl,
            condition = { it.trim().takeIf { it.length > 2 } },
            onChange = { makeChange() })

        val urlHome = EFieldTextString(
            property = data::homeUrl,
            condition = { it.trim().takeIf { it.length > 2 } },
            onChange = { makeChange() })

        val urlDocs = EFieldTextString(
            property = data::docsUrl,
            condition = { it.trim().takeIf { it.length > 2 } },
            onChange = { makeChange() })

        val imageIco = EFieldRegularLong(
            property = data::imageIco,
            onChange = { makeChange() })

        val imageLogoSquare = EFieldRegularLong(
            property = data::imageLogoSquare,
            onChange = { makeChange() })

        val imageLogoHorizontal = EFieldRegularLong(
            property = data::imageLogoHorizontal,
            onChange = { makeChange() })

        val imageLogoDocsLight = EFieldRegularLong(
            property = data::imageLogoDocsLight,
            onChange = { makeChange() })

        val imageLogoDocsDark = EFieldRegularLong(
            property = data::imageLogoDocsDark,
            onChange = { makeChange() })

        private val _change = mutableStateOf(false)
        val change: State<Boolean> get() = _change

        fun canBeLocked() = editFieldsText.none { it.err } && !(CurrencyType.UNDEFINED match priceCurrencyType.state)

        private val editFieldsText
            get() = listOf(
                name,

                companyId,
                companyVat,
                companyName,
                companyAddr,

                responsibleTel,
                responsibleWww,
                responsibleName,
                responsibleEmail,

                domainTitle,

                urlControl,
                urlGame,
                urlHome,
                urlDocs
            )
        private val editFields
            get() = listOf(
                pricePct,
                priceCurrencyType,
                pricePersonName,
                pricePersonMail,

                imageIco,
                imageLogoSquare,
                imageLogoHorizontal,
                imageLogoDocsLight,
                imageLogoDocsDark,
            ) + editFieldsText

        fun applyChanges() {
            editFields.forEach { it.applyChanges() }
            makeChange()
        }

        private fun makeChange() {
            _change.value = editFields.any { it.change } && (!name.err)
        }

        fun makeLock() {
            editable = false
        }
    }
}
